import { ReactNode } from 'react'
import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from '../AuthContext'
import { LoadingStatus } from 'shared/types'
import {
  ReauthenticationStep,
  useReauthentication,
} from '../../hooks/use-reauthentication'
import ReauthenticationDialogHeader from './ReauthenticationDialogHeader'
import ReauthenticationNotices from './ReauthenticationNotices'
import { BusyProgress, StyledDialog } from './ReauthenticationDialog.style'
import { usePasswordFormProps } from './ReauthenticationDialog.utils'
import { CodeStep, LinkStep, OAuthStep, PasswordStep } from './steps'

const TITLE_ID = 'reauthentication-dialog-title'

interface ReauthenticationDialogProps {
  title: ReactNode
  // What the reauthentication is for, e.g. the page's name
  subtitle?: ReactNode
  message: ReactNode
  // Consequence of the action the user reauthenticates for
  warning?: ReactNode
  closeLabel?: ReactNode
  onClose: () => void
  onReauthenticated: () => Promise<void> | void
}

// Not closed by clicking outside, as closing may also leave the current mode
const ReauthenticationDialog = ({
  title,
  subtitle,
  message,
  warning,
  closeLabel,
  onClose,
  onReauthenticated,
}: ReauthenticationDialogProps) => {
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const email = user?.email ?? ''
  const {
    step,
    oauthProvider,
    loading,
    error,
    isLinkResent,
    isSendingLink,
    reauthenticateWithPassword,
    reauthenticateWithOAuthProvider,
    verifyCode,
    sendLink,
    goBack,
  } = useReauthentication(onReauthenticated)
  const passwordFormProps = usePasswordFormProps(
    reauthenticateWithPassword,
    error
  )
  const isPending = loading === LoadingStatus.Pending
  // Stays busy after success, until the dialog is closed or the page reloads
  const isBusy = isPending || loading === LoadingStatus.Succeeded

  const notices = <ReauthenticationNotices warning={warning} error={error} />
  // The code step shows its errors with the code field
  const codeStepNotices = <ReauthenticationNotices warning={warning} />

  const closeButton = (
    <Button
      variant="outlined"
      onClick={onClose}
      disabled={isPending || isSendingLink}
    >
      {closeLabel ?? t('cancel')}
    </Button>
  )

  const renderStep = () => {
    if (step === ReauthenticationStep.TwoFactorCode)
      return (
        <CodeStep
          email={email}
          notices={codeStepNotices}
          error={error}
          isBusy={isBusy}
          onSubmit={verifyCode}
          onBack={goBack}
        />
      )

    if (step === ReauthenticationStep.EmailLink)
      return (
        <LinkStep
          email={email}
          notices={notices}
          isLinkResent={isLinkResent}
          isBusy={isBusy}
          isSendingLink={isSendingLink}
          onBack={goBack}
          onResend={sendLink}
        />
      )

    if (oauthProvider)
      return (
        <OAuthStep
          provider={oauthProvider}
          message={message}
          notices={notices}
          closeButton={closeButton}
          loading={loading}
          isBusy={isBusy}
          onSignIn={reauthenticateWithOAuthProvider}
        />
      )

    return (
      <PasswordStep
        formProps={passwordFormProps}
        email={email}
        message={message}
        notices={notices}
        closeButton={closeButton}
        isBusy={isBusy}
        isSendingLink={isSendingLink}
        onSendLink={sendLink}
      />
    )
  }

  return (
    <StyledDialog open fullWidth maxWidth="xs" aria-labelledby={TITLE_ID}>
      {isBusy && <BusyProgress />}
      <ReauthenticationDialogHeader
        titleId={TITLE_ID}
        title={title}
        subtitle={subtitle}
        step={step}
        isBusy={isBusy}
      />
      {renderStep()}
    </StyledDialog>
  )
}

export default ReauthenticationDialog
